import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DateUtils,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LightSplitLayoutProps,
  mixins,
  TranslateUtils,
  useDefaultScrollTo,
  useLocalStorage,
} from '@wallaby-ui/core';
import {ChartColor, HaidiSchema, LightChartLegend2, useFormatters, usePermissions, useTableData, useTableParams} from '@wallaby-ui/document';
import * as React from 'react';
import {useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {defineMessages, useIntl} from 'react-intl';

import {DashboardColumn} from './DashboardColumn';
import {DashboardMonthChart} from './DashboardMonthChart';
import {useChartHiddenMap} from '../../common/useChartHiddenMap';

const I18N = defineMessages({
  PAGE_TITLE: {
    id: 'hai.dashboard.pageTitle',
    defaultMessage: 'Home page',
    description: 'title for hai dashboard page',
  },
});

interface OwnProps {
  config: ConfigObjectType;
}

export type DashboardDataType = HaidiSchema.components['schemas']['HaiDashboardDto'];
export const HaidiDashboardLayoutContainer: React.FC<OwnProps> = (props) => {
  const {config} = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const intl = useIntl();
  const formatters = useFormatters();
  const {tEnum} = formatters;
  const {isLoading: rolesLoading, haiByOrganizationReportLevel} = usePermissions(config);
  const dashboardApiParams = useTableParams({
    haiByOrganizationReportLevel,
  });
  // NOTE: we assume isLoading is true if data.xxx is null
  const {data, isLoading: _} = useTableData<any, DashboardDataType>(
    config.service.url.haiDashboardService,
    dashboardApiParams,
    'content',
    !rolesLoading
  );

  const splitA: LightSplitLayoutProps = {
    storageId: '/haiDashboard.splitA',
    initSizes: [0, 100],
    minSizes: [300, 0],
    noGrow: [false, false],
    isVertical: true,
  };
  const splitB: LightSplitLayoutProps = {
    storageId: '/haiDashboard.splitB',
    initSizes: [50, 50],
    minSizes: [0, 0],
    isVertical: false,
  };
  const {hiddenMap: monthChartHiddenMap, onSetHiddenMap: onSetMonthChartHiddenMap, applyHidden: applyMonthChartHidden} =
    useChartHiddenMap();
  const monthChartColors: ChartColor[] = applyMonthChartHidden([
    {
      id: 'YES',
      color: '#ff8717',
      label: tEnum('State', 'YES'),
    },
    {
      id: 'COL',
      color: '#6fa9ff',
      label: tEnum('State', 'COL'),
    },
  ]);
  const reportedColor: ChartColor = {
    color: '#6fa9ff',
    label: tEnum('State', 'REP'),
  };
  const workInProgressColor: ChartColor = {
    color: '#6fa9ff',
    label: tEnum('State', 'WIP'),
  };
  const lastWeekMonthIso = useMemo(() => DateUtils.lastWeekMonthIso(), []);
  const [leftDefaultScroll, setLeftDefaultScroll] = useLocalStorage(`/haidiDashboard.leftScroll`, 0);
  const leftScrollRef = useDefaultScrollTo(leftDefaultScroll);
  const [rightDefaultScroll, setRightDefaultScroll] = useLocalStorage(`/haidiDashboard.rightScroll`, 0);
  const rightScrollRef = useDefaultScrollTo(rightDefaultScroll);
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={TranslateUtils.translate(I18N.PAGE_TITLE, intl)} />
      <LightSplitLayout {...splitA}>
        <div css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName={'haidiDashboardIconMultiColor'}
            label={'dashboard.confirmedHaisAndColonizations'}
            useFixedViewBox='0 0 16 16'
            iconCss={muiStyles.headerIcon}
          >
            <LightChartLegend2 items={monthChartColors} hiddenMap={monthChartHiddenMap} onSetHiddenMap={onSetMonthChartHiddenMap} />
          </IconHeaderWithSearch>
          <DashboardMonthChart
            confirmedHais={data?.confirmedHaisByMonth}
            colonizations={data?.colonizedHaisByMonth}
            chartColors={monthChartColors}
            formatters={formatters}
          />
        </div>
        <LightSplitLayout {...splitB}>
          <div
            ref={leftScrollRef}
            css={muiStyles.contentBox}
            onScroll={(event) => {
              setLeftDefaultScroll((event.target as HTMLElement).scrollTop);
            }}
          >
            <DashboardColumn
              linksLabelKey='dashboard.reportedHais'
              linkStateFilter='REP'
              linksData={{
                total: data?.countOfReportedHaisTotal,
                lastWeek: data?.countOfReportedHaisLastWeek,
                olderThanLastWeek: data?.countOfReportedHaisOlderThanLastWeek,
                olderThanLastMonth: data?.countOfReportedHaisOlderThanLastMonth,
              }}
              dataByType={data?.countOfReportedHaisByType}
              dataByOrigin={data?.countOfReportedHaisByOrganization}
              noHaisLabelKey='dashboard.noData.reportedHais'
              recentHaisLabelKey='dashboard.recentReportedHais'
              recentHais={data?.lastReportedHais}
              noRecentHaisLabelKey={'dashboard.noData.reportedHais'}
              barChartColor={reportedColor}
              formatters={formatters}
              lastWeekMonthIso={lastWeekMonthIso}
              config={config}
            />
          </div>
          <div
            ref={rightScrollRef}
            css={muiStyles.contentBox}
            onScroll={(event) => {
              setRightDefaultScroll((event.target as HTMLElement).scrollTop);
            }}
          >
            <DashboardColumn
              linksLabelKey='dashboard.workInProgressHais'
              linkStateFilter='WIP'
              linksData={{
                total: data?.countOfInProgressHaisTotal,
                lastWeek: data?.countOfInProgressHaisLastWeek,
                olderThanLastWeek: data?.countOfInProgressHaisOlderThanLastWeek,
                olderThanLastMonth: data?.countOfInProgressHaisOlderThanLastMonth,
              }}
              dataByType={data?.countOfInProgressHaisByType}
              dataByOrigin={data?.countOfInProgressHaisByOrganization}
              noHaisLabelKey='dashboard.noData.workInProgressHais'
              recentHaisLabelKey='dashboard.recentWorkInProgressHais'
              recentHais={data?.lastInProgressHais}
              noRecentHaisLabelKey={'dashboard.noData.workInProgressHais'}
              barChartColor={workInProgressColor}
              formatters={formatters}
              lastWeekMonthIso={lastWeekMonthIso}
              config={config}
            />
          </div>
        </LightSplitLayout>
      </LightSplitLayout>
    </div>
  );
};
const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
});
